import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatEtaWindow } from "../lib/eta";
import { Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, Home, Clock, CalendarDays } from "lucide-react";

const STAGES = [
  { key: "awaiting_payment", label: "Awaiting payment", icon: Clock, color: "text-slate-500" },
  { key: "received", label: "Received", icon: Package, color: "text-blue-600" },
  { key: "packed", label: "Packed", icon: Package, color: "text-amber-600" },
  { key: "shipped", label: "Shipped", icon: Truck, color: "text-purple-600" },
  { key: "delivered", label: "Delivered", icon: Home, color: "text-emerald-600" },
];

const stageIndex = (s) => STAGES.findIndex((x) => x.key === s);

const Stepper = ({ status }) => {
  const idx = stageIndex(status);
  return (
    <div className="flex items-center gap-2" data-testid="order-status-tracker-stepper">
      {STAGES.slice(1).map((st, i) => {
        const done = i + 1 <= idx;
        const active = i + 1 === idx;
        const Icon = st.icon;
        return (
          <React.Fragment key={st.key}>
            <div className={`flex flex-col items-center ${done ? "text-indigo-600" : "text-slate-400"}`}>
              <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${done ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 bg-white"} ${active ? "ring-4 ring-indigo-200 animate-pulse" : ""}`}>
                {done && !active ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="text-[10px] font-semibold mt-1">{st.label}</div>
            </div>
            {i < STAGES.length - 2 && <div className={`flex-1 h-0.5 ${i + 1 < idx ? "bg-indigo-600" : "bg-slate-200"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/orders"); setOrders(data); } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-2">My orders</h1>
      <p className="text-slate-600 mb-8">Live wholesaler auto-fulfillment status. Updates every few seconds.</p>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-slate-200">
          <div className="font-display text-xl font-bold">No orders yet</div>
          <div className="text-sm text-slate-500 mt-1">Place your first order to see live fulfillment.</div>
          <Link to="/shop" className="inline-block mt-4 px-5 py-2 rounded-full bg-indigo-600 text-white text-sm font-semibold">Shop supplies</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Link to={`/orders/${o.order_id}`} key={o.order_id} className="block p-6 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all" data-testid={`order-card-${o.order_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <div className="font-mono text-xs text-slate-500">{o.order_id}</div>
                  <div className="font-display font-bold text-lg">{o.items.length} item{o.items.length > 1 ? "s" : ""} · ${o.total.toFixed(2)}</div>
                  <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {o.payment_status}
                  </span>
                  {o.is_wholesale && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-indigo-50 text-indigo-700">wholesale</span>}
                </div>
              </div>

              <div className="flex gap-2 mb-4 overflow-x-auto">
                {o.items.map((it, i) => (
                  <img key={i} src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                ))}
              </div>

              {o.fulfillment_status !== "delivered" && formatEtaWindow(o) && (
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-full mb-3" data-testid={`order-eta-${o.order_id}`}>
                  <CalendarDays className="w-3 h-3" />
                  Est. delivery {formatEtaWindow(o)}
                </div>
              )}

              <Stepper status={o.fulfillment_status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
